import { ApiError } from '@/api/types';
import { t } from '@/i18n';

/** What to tell the person when sending something failed; `fallback` covers everything unexpected. */
export function sendErrorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  switch (error.code) {
    case 'payload_too_large':
      return t('errors.tooLarge');
    case 'storage_full':
      return error.message;
    case 'rate_limited':
      return t('errors.rateLimited');
    case 'network':
      return error.message;
    default:
      return fallback;
  }
}

/** What an error notice says: a title, what to do next, and (for a request that got no answer) where it was going. */
export interface Notice {
  readonly title: string;
  readonly text?: string | undefined;
  readonly detail?: string | undefined;
}

/**
 * The notice for something that failed. A request that never got an answer is the same everywhere: “Can't reach the
 * bridge” and what to press again (`action` is the label of the button the person pressed). Anything else keeps its own words.
 */
export function noticeOf(error: unknown, fallback: string, action: string): Notice {
  if (error instanceof ApiError && error.code === 'network') {
    return { title: t('errors.unreachable'), text: t('errors.checkConnection', { action }), detail: error.detail };
  }
  return { title: sendErrorText(error, fallback) };
}
